import { BusinessHoursInput } from "@fullcalendar/core";
import {
  addDays,
  endOfDay,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  parse,
  startOfDay,
  startOfWeek,
  subMinutes,
} from "date-fns";

import { CalendarEvent, CalendarView } from "@ds-developer/calendar";
import { WeekDayNumber } from "@ds-developer/constants";

import { WorkingHour } from "@hubcapp/types";

const DAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export const isEventVisibleInDay = (
  date: Date,
  event: CalendarEvent,
): boolean =>
  isSameDay(new Date(event.start as Date), date) ||
  isSameDay(new Date(event.end as Date), date);

export const filterEventsForRange = ({
  events = [],
  initialDate,
  numberDays,
}: {
  events: any[];
  initialDate: Date;
  numberDays: number;
}) =>
  events.filter(
    event =>
      [...Array(numberDays)].filter((_, dayNum) =>
        isEventVisibleInDay(addDays(initialDate, dayNum), event),
      ).length,
  );

/**
 * determine earliest possible event slot for date
 * @param date
 * @param slotMinTimeHours
 */
export const earliestEventTime = (
  date: Date,
  slotMinTimeHours: number,
): Date => {
  const startTimeOffset = format(date, "xx");
  const hours = ("0" + slotMinTimeHours).slice(-2);
  return new Date(
    `${format(date, "yyyy-MM-dd")}T${hours}:00:00${startTimeOffset}`,
  );
};

/**
 * determine latest possible event slot for date
 * @param date
 * @param slotMaxTimeHours
 */
export const latestEventTime = (date: Date, slotMaxTimeHours: number) => {
  const startTimeOffset = format(date, "xx");
  const hours = ("0" + (slotMaxTimeHours - 1)).slice(-2);
  return new Date(
    `${format(date, "yyyy-MM-dd")}T${hours}:59:59${startTimeOffset}`,
  );
};

/**
 * Return number of days that should be visible for a given CalendarView
 * @param view
 */
export const numberDaysForView = (view: CalendarView) => {
  if (view === CalendarView.day) return 1;
  if (view === CalendarView.week) return 7;
  return 3;
};

export const getCalenderEventsQueryInput = (date: Date, view: CalendarView) => {
  if (view === CalendarView.week) {
    return {
      startDate: startOfDay(startOfWeek(date, { weekStartsOn: 1 })),
      endDate: endOfDay(endOfWeek(date, { weekStartsOn: 1 })),
    };
  }

  if (view === CalendarView.threeDay) {
    return {
      startDate: startOfDay(date),
      endDate: endOfDay(addDays(date, 3)),
    };
  }

  return {
    startDate: startOfDay(date),
    endDate: endOfDay(date),
  };
};

export const constructWorkingDays = (workingHours?: BusinessHoursInput) =>
  workingHours && Array.isArray(workingHours)
    ? workingHours.reduce<WeekDayNumber[]>((days, hours) => {
        days.push(...hours.daysOfWeek);
        return days;
      }, [])
    : ([0, 1, 2, 3, 4, 5, 6] as WeekDayNumber[]);

export const constructWorkingHours = (workingHours?: WorkingHour[]) =>
  workingHours &&
  workingHours
    .filter(({ start, end }) => start && end)
    .map(({ start, end, day }: WorkingHour) => ({
      daysOfWeek: [DAY_INDEX[day]],
      startTime: start || 0,
      endTime: end || 0,
    }));

export const getEarliestStartTime = (workingHours?: WorkingHour[]) =>
  workingHours
    ? workingHours
        .map(workingHour => workingHour.start || "9:00:00")
        .reduce((earliest, startTime) => {
          const earliestStartDateTime = parse(earliest, "HH:mm", new Date());
          const startDateTime = subMinutes(
            parse(startTime, "HH:mm:ss", new Date()),
            20,
          );

          if (isBefore(startDateTime, earliestStartDateTime)) {
            earliest = format(startDateTime, "HH:mm");
          }
          return earliest;
        }, "08:40")
    : "08:40";
